import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import {
  isAdmin,
  fetchFamilies,
  getUserAccess,
  setUserAccess,
  clearUserOverride,
} from '@/services/adminService'
import { getEffectiveEntitlements } from '@/services/entitlementsService'
import type {
  AdminUserRow,
  UserAccessData,
  SetUserAccessParams,
  EffectiveEntitlements,
} from '@/types/admin'
import type { Family } from '@/types/database'

interface SelectedUser {
  family: UserAccessData['family']
  override: UserAccessData['override']
  effective: EffectiveEntitlements
}

interface AdminContextValue {
  isAdminUser: boolean
  families: AdminUserRow[]
  isLoadingFamilies: boolean
  searchQuery: string
  selectedUser: SelectedUser | null
  isLoadingUser: boolean
  isSaving: boolean
  error: string | null
  setSearchQuery: (query: string) => void
  searchFamilies: () => Promise<void>
  selectUser: (authId: string) => Promise<void>
  clearSelectedUser: () => void
  saveAccess: (params: Omit<SetUserAccessParams, 'userId'>) => Promise<boolean>
  clearOverride: () => Promise<boolean>
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined)

export function AdminProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const isAdminUser = isAdmin(user?.id)

  const [families, setFamilies] = useState<AdminUserRow[]>([])
  const [isLoadingFamilies, setIsLoadingFamilies] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const searchFamilies = useCallback(async () => {
    setIsLoadingFamilies(true)
    setError(null)

    const { data, error: fetchError } = await fetchFamilies(searchQuery)

    if (fetchError) {
      setError(fetchError.message)
      setFamilies([])
    } else {
      setFamilies(data || [])
    }

    setIsLoadingFamilies(false)
  }, [searchQuery])

  const selectUser = useCallback(async (authId: string) => {
    setIsLoadingUser(true)
    setError(null)

    const { data, error: fetchError } = await getUserAccess(authId)

    if (fetchError) {
      setError(fetchError.message)
      setSelectedUser(null)
    } else if (data) {
      const effective = getEffectiveEntitlements(
        data.family as Family | null,
        data.override
      )
      setSelectedUser({
        family: data.family,
        override: data.override,
        effective,
      })
    }

    setIsLoadingUser(false)
  }, [])

  const clearSelectedUser = useCallback(() => {
    setSelectedUser(null)
  }, [])

  const saveAccess = useCallback(async (params: Omit<SetUserAccessParams, 'userId'>): Promise<boolean> => {
    if (!selectedUser?.family?.auth_id) return false

    setIsSaving(true)
    setError(null)

    const { error: saveError } = await setUserAccess({
      userId: selectedUser.family.auth_id,
      ...params,
    })

    if (saveError) {
      setError(saveError.message)
      setIsSaving(false)
      return false
    }

    // Refresh the selected user data
    await selectUser(selectedUser.family.auth_id)
    // Also refresh the families list to update the override indicator
    await searchFamilies()

    setIsSaving(false)
    return true
  }, [selectedUser, selectUser, searchFamilies])

  const clearOverrideHandler = useCallback(async (): Promise<boolean> => {
    if (!selectedUser?.family?.auth_id) return false

    setIsSaving(true)
    setError(null)

    const { error: clearError } = await clearUserOverride(selectedUser.family.auth_id)

    if (clearError) {
      setError(clearError.message)
      setIsSaving(false)
      return false
    }

    // Refresh the selected user data
    await selectUser(selectedUser.family.auth_id)
    // Also refresh the families list to update the override indicator
    await searchFamilies()

    setIsSaving(false)
    return true
  }, [selectedUser, selectUser, searchFamilies])

  return (
    <AdminContext.Provider
      value={{
        isAdminUser,
        families,
        isLoadingFamilies,
        searchQuery,
        selectedUser,
        isLoadingUser,
        isSaving,
        error,
        setSearchQuery,
        searchFamilies,
        selectUser,
        clearSelectedUser,
        saveAccess,
        clearOverride: clearOverrideHandler,
      }}
    >
      {children}
    </AdminContext.Provider>
  )
}

export function useAdmin() {
  const context = useContext(AdminContext)
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
}
